import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { InfoCard, InfoCardRowDef } from '~components';

import { castMinutesToTimeString } from '../../../../shared/utility';
import PoliciesService from '../../services/policyService';

const service = new PoliciesService();

interface PolicyBookingInfoCardProps {
	id?: string;
}

const PolicyBookingInfoCard = ({ id }: PolicyBookingInfoCardProps) => {
	const { t } = useTranslation();

	const { data, isLoading, error } = useSWR(id != null && [service.basePath, id], ([_, id]) =>
		service.getPolicyById(id)
	);

	const rows: InfoCardRowDef[] | undefined =
		data != null ?
			[
				{
					headerName: t('ui.label.requiresConfirmation'),
					value: data.requireHostConfirmation,
					valueFormatter: (value: boolean) => t(value ? 'ui.enabled' : 'ui.disabled'),
				},
				{
					headerName: t('ui.label.bookingHours'),
					value: data.openHoursRestrictionType, // TODO
					valueFormatter: (value) => {
						let tKey: string;
						switch (value) {
							case 'create':
								tKey = 'ui.enabled';
								break;
							case 'usage':
								tKey = 'ui.required';
								break;
							default:
								// case 'none'
								tKey = 'ui.disabled';
								break;
						}
						return t(tKey);
					},
				},
				{
					headerName: t('ui.label.minDuration'),
					value: data.minBookingDurationMinutes,
					valueFormatter: (value?: number) =>
						value != null ? castMinutesToTimeString(value) : '-',
				},
				{
					headerName: t('ui.label.maxDuration'),
					value: data.maxBookingDurationMinutes,
					valueFormatter: (value?: number) =>
						value != null ? castMinutesToTimeString(value) : '-',
				},
				{
					headerName: t('views.addPolicy.bookingConfiguration.section.header.inUse'),
					value: data.requireOvertimeValidation,
					valueFormatter: (value: boolean) => t(value ? 'ui.enabled' : 'ui.disabled'),
				},
				{
					headerName: t('ui.label.futureLimit'),
					value: data.maxDurationBeforeStartMinutes,
					valueFormatter: (value: number) => (value > 0 ? castMinutesToTimeString(value) : '-'),
				},
				{
					headerName: t('ui.label.cooldownPeriod'),
					value: data.cooldownBufferMinutes,
					valueFormatter: (value: number) => (value > 0 ? castMinutesToTimeString(value) : '-'),
				},
				{
					headerName: t('ui.label.cancellationLimit'),
					value: data.minDurationBeforeCancelMinutes,
					valueFormatter: (value: number) => (value > 0 ? castMinutesToTimeString(value) : '-'),
				},
				{
					headerName: t('views.policyManagement.details.policyDetail.graceiPariod'),
					value: data.overtimeGracePeriodMinutes,
					valueFormatter: (value: number) => (value > 0 ? castMinutesToTimeString(value) : '-'),
				},
				{
					headerName: t('ui.label.extensionPossible'),
					value: data.allowExtending,
					valueFormatter: (value: boolean) => t(value ? 'ui.enabled' : 'ui.disabled'),
				},
				{
					headerName: t('ui.label.verificationRequired'),
					value: data.requiredLicenseStatus,
					valueFormatter: (value: string) =>
						t(['validation', 'verification'].includes(value) ? 'ui.enabled' : 'ui.disabled'),
				},
				{
					headerName: t('views.policyManagement.details.policyDetail.useNow'),
					value: data.requiredLicenseStatus,
					valueFormatter: (value: string) =>
						t(['notApplicable', 'verification'].includes(value) ? 'ui.disabled' : 'ui.enabled'),
				},
				{
					headerName: t('minimumAge'),
					value: data.minimumAge,
				},
				{
					headerName: t('ui.label.dutchLicense'),
					value: data.requireDutchLicense,
					valueFormatter: (value: boolean) => t(value ? 'ui.enabled' : 'ui.disabled'),
				},
				{
					headerName: t('ui.label.licensePlate'),
					value: data.requireParkingLicensePlateNumber,
					valueFormatter: (value) => {
						let tKey: string;
						switch (value) {
							case 'optional':
								tKey = 'ui.enabled';
								break;
							case 'required':
								tKey = 'ui.required';
								break;
							default:
								// case 'notApplicable'
								tKey = 'ui.disabled';
								break;
						}
						return t(tKey);
					},
				},
			]
		:	undefined;

	return (
		<InfoCard
			title={t('views.policyDetail.summary.cardHeaders.bookingPolicies')}
			rows={rows ?? []}
			error={error != null}
			loading={isLoading}
		/>
	);
};

export default PolicyBookingInfoCard;
